import React from 'react';
import {
  IonAccordionGroup,
  IonAccordion,
  IonItem,
  IonIcon,
  IonLabel,
  IonThumbnail,
  IonImg,
} from '@ionic/react';
import { trash, add } from 'ionicons/icons';

import { MachineSession } from '../../../datamodels';
import { deleteMachineSession as deleteSessionService } from '../../../services/TrainingSessionService';
import SetRow from './SetRow';
import { getExerciseTrackingMode, trackingLabel } from '../../../utils/ExerciseTracking';

interface MachineSessionsAccordionProps {
  machineSessions: MachineSession[];
  machineNames: { [key: string]: string };
  machineImages: { [key: string]: string };
  sessionId: string | null;
  handleRemoveSet: (sessionIndex: number, setIndex: number) => void;
  setSelectedSessionIndex: React.Dispatch<React.SetStateAction<number | null>>;
  setShowTextModal: React.Dispatch<React.SetStateAction<boolean>>;
  deleteMachineSession: typeof deleteSessionService;
  setMachineSessions: React.Dispatch<React.SetStateAction<MachineSession[]>>;
}

const FALLBACK_IMG = 'assets/pl_icon/icon_f_matt.png';

const MachineSessionsAccordion: React.FC<MachineSessionsAccordionProps> = ({
  machineSessions,
  machineNames,
  machineImages,
  sessionId,
  handleRemoveSet,
  setSelectedSessionIndex,
  setShowTextModal,
  deleteMachineSession,
  setMachineSessions,
}) => {
  const commitReps = (si: number, ui: number, val: number) => {
    setMachineSessions(prev => {
      const next = [...prev];
      next[si].sets[ui].reps = val;
      return next;
    });
  };

  const commitWeight = (si: number, ui: number, val: number) => {
    setMachineSessions(prev => {
      const next = [...prev];
      next[si].sets[ui].weight = val;
      return next;
    });
  };

  return (
    <IonAccordionGroup>
      {machineSessions.map((session, si) => {
        const machineId = session.machine_ref.id;
        const title = machineNames[machineId] || 'Loading…';
        const extra =
          session.exercise_name && session.exercise_name !== title
            ? ` – ${session.exercise_name}`
            : '';

        const imgSrc = machineImages[machineId] || FALLBACK_IMG;
        const trackingMode = getExerciseTrackingMode(title, session.exercise_name ? { name: session.exercise_name } : undefined);

        return (
          <IonAccordion key={session.id} value={session.id}>
            <IonItem slot="header" color="light" className="machineHeader">
              <IonThumbnail slot="start" className="machineThumbWrap">
                <IonImg src={imgSrc} alt={title} className="machineThumbImg" />
              </IonThumbnail>

              <IonLabel className="machineHeaderLabel">
                <div className="machineTitle">{title}</div>
                {extra && <div className="machineSubtitle">{extra}</div>}
              </IonLabel>

              <IonIcon
                icon={trash}
                slot="end"
                className="machineDelete"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  deleteMachineSession(
                    session.id,
                    sessionId!,
                    machineSessions,
                    setMachineSessions
                  );
                }}
              />
            </IonItem>

            <div className="ion-padding" slot="content">
              <IonItem lines="none">
                <IonLabel
                  slot="start"
                  style={{ fontSize: '.75rem', fontWeight: 'bold' }}
                >
                  Set
                </IonLabel>
                <IonLabel style={{ fontSize: '.75rem', fontWeight: 'bold' }}>
                  Reps
                </IonLabel>
                <IonLabel style={{ fontSize: '.75rem', fontWeight: 'bold' }}>
                  {trackingLabel(trackingMode)}
                </IonLabel>
              </IonItem>

              {session.sets.map((set, ui) => (
                <SetRow
                  key={set.set_number}
                  sessionIndex={si}
                  setIndex={ui}
                  reps={set.reps}
                  weight={set.weight}
                  trackingMode={trackingMode}
                  onRepsCommit={commitReps}
                  onWeightCommit={commitWeight}
                  handleRemoveSet={handleRemoveSet}
                />
              ))}

              <IonItem
                button
                onClick={() => {
                  setSelectedSessionIndex(si);
                  setShowTextModal(true);
                }}
              >
                <IonLabel>Add New Set</IonLabel>
                <IonIcon icon={add} slot="end" />
              </IonItem>
            </div>
          </IonAccordion>
        );
      })}
    </IonAccordionGroup>
  );
};

export default MachineSessionsAccordion;
